import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, onSessionExpired, tokens } from '../api/client';
import type { AuthUser, LoginResponse } from '../api/types';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, organization?: string) => Promise<void>;
  logout: () => void;
  /** True when the user holds every listed permission. */
  can: (...permissions: string[]) => boolean;
  /** True when the user holds at least one. */
  canAny: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Rehydrate on boot: a stored token is not proof of a live session, so ask
  // the server who we are rather than trusting a cached user object.
  useEffect(() => {
    let cancelled = false;
    if (!tokens.access) {
      setLoading(false);
      return;
    }
    api<AuthUser>('/auth/me')
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        tokens.clear();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The API client signals a failed refresh; drop to the login screen.
  useEffect(() => onSessionExpired(() => setUser(null)), []);

  const login = useCallback(async (email: string, password: string, organization?: string) => {
    const res = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      anonymous: true,
      body: { email, password, ...(organization ? { organization } : {}) },
    });
    tokens.set(res.access_token, res.refresh_token);
    setUser(res.user);
  }, []);

  const logout = useCallback(() => {
    // Tell the server first so the session is recorded as signed out and its
    // tokens stop working. Fire-and-forget: signing out never waits on, or
    // fails because of, the network. keepalive lets it finish if the tab closes.
    const access = tokens.access;
    const refresh = tokens.refresh;
    if (access || refresh) {
      void fetch('/api/v1/auth/logout', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
        },
        body: JSON.stringify(refresh ? { refresh_token: refresh } : {}),
      }).catch(() => {});
    }
    tokens.clear();
    setUser(null);
  }, []);

  // Auto-logout after 15 minutes of background inactivity (tab switched/hidden)
  useEffect(() => {
    if (!user) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        localStorage.setItem('crm_tab_hidden_at', Date.now().toString());
        timeoutId = setTimeout(() => {
          logout();
        }, 15 * 60 * 1000);
      } else {
        const hiddenAtStr = localStorage.getItem('crm_tab_hidden_at');
        const now = Date.now();
        
        if (hiddenAtStr) {
          const hiddenAt = parseInt(hiddenAtStr, 10);
          if (!isNaN(hiddenAt) && now - hiddenAt >= 15 * 60 * 1000) {
            logout();
          }
        }
        
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = null;
        localStorage.removeItem('crm_tab_hidden_at');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, logout]);

  const value = useMemo<AuthState>(() => {
    const granted = new Set(user?.permissions ?? []);
    return {
      user,
      loading,
      login,
      logout,
      can: (...permissions) => permissions.every((p) => granted.has(p)),
      canAny: (...permissions) => permissions.some((p) => granted.has(p)),
    };
  }, [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Renders children only when the permission is held.
 *
 * Hiding a control is a courtesy, not a security boundary — the API rejects the
 * call regardless. It exists so users are not shown buttons that will fail.
 */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  const list = Array.isArray(permission) ? permission : [permission];
  return <>{can(...list) ? children : fallback}</>;
}
